/**
 * useMeasurementConfig — the measurement module's ops hook (the ONLY write path).
 *
 * MODULE-CONTRACT §2. The module's state IS its flow DTO at
 * `productConfigs["${cartItemId}|measurement"]`; this hook wraps it with a typed
 * read (field-merged over the default) and a single `patch` writer. No imperative
 * handle, no serialize/hydrate — `serializeDraft` / `hydrateFromDraft` already
 * round-trip `productConfigs`.
 *
 * `patch` is inherently the USER-EDIT boundary — hydration never calls it (§3) —
 * so it un-confirms unconditionally via `markConfigDirty`.
 */

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { useFlowConfig } from '../useFlowConfig'
import { MEASUREMENT_DEFAULT, measurementIsComplete, type MeasurementDraftDTO } from './measurement.dto'

export interface UseMeasurementConfig {
  /** Field-complete DTO — safe to read every field, even against a partial or
   *  absent persisted entry (§2 partial-DTO invariant). */
  value: MeasurementDraftDTO
  /** Merge-and-write a partial DTO. Idempotency-guarded by `setConfig`; flips the
   *  tab back to unconfirmed via `markConfigDirty`. */
  patch: (partial: Partial<MeasurementDraftDTO>) => void
  isComplete: boolean
}

export function useMeasurementConfig(cartItemId: CartItemId): UseMeasurementConfig {
  const { value, patch, isComplete } = useFlowConfig(cartItemId, 'measurement', MEASUREMENT_DEFAULT, measurementIsComplete)
  return { value, patch, isComplete }
}
